import React from 'react';
import { AlertTriangle, Clock } from 'lucide-react';
import { useBetaEnd } from '../context/BetaEndContext';

/**
 * Pulsing gold-glow chip shown in the sidebar once the beta-ended flow is
 * "paused" (user dismissed the modal). Clicking re-opens the modal so they
 * can pick a plan. Shows live countdown when discount is still active.
 */
export default function BetaEndedBanner({ collapsed = false }) {
  const { status, dismissed, setDismissed, discountExpiresIn } = useBetaEnd();
  if (!status?.show_end_modal || !dismissed) return null;

  const fmt = (secs) => {
    if (!secs || secs <= 0) return null;
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    return `${h}h ${String(m).padStart(2, '0')}m`;
  };
  const timeLeft = status.discount_active ? fmt(discountExpiresIn) : null;

  return (
    <button
      onClick={() => setDismissed(false)}
      data-testid="beta-ended-banner"
      className="relative w-full text-left group"
      title="Choose a plan to keep using your workspace"
    >
      <div
        className="absolute inset-0 rounded-xl opacity-70 group-hover:opacity-100 transition-opacity animate-pulse pointer-events-none"
        style={{ background: 'radial-gradient(circle at 50% 50%, rgba(251,191,36,0.45), transparent 70%)', filter: 'blur(12px)' }}
      />
      <div
        className="relative rounded-xl px-3 py-2.5 flex items-center gap-2"
        style={{
          background: 'linear-gradient(135deg, rgba(251,191,36,0.18) 0%, rgba(245,158,11,0.10) 100%)',
          border: '1.5px solid rgba(251,191,36,0.55)',
          boxShadow: '0 0 16px -2px rgba(251,191,36,0.4), inset 0 0 0 1px rgba(251,191,36,0.15)',
        }}
      >
        <AlertTriangle size={collapsed ? 14 : 14} className="text-amber-300 flex-shrink-0" />
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-extrabold tracking-[0.18em] text-amber-200 uppercase">Beta Program Ended</p>
            {timeLeft ? (
              <p className="text-[11px] font-semibold text-amber-100 flex items-center gap-1 tabular-nums mt-0.5">
                <Clock size={10} /> {timeLeft} left on deal
              </p>
            ) : (
              <p className="text-[10px] text-amber-100/80 mt-0.5">Choose a plan to continue</p>
            )}
          </div>
        )}
      </div>
    </button>
  );
}
